import { Fragment, memo, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import {
    NavigateNext as NavigateNextIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { green } from "@mui/material/colors";
import { v4 as uuidv4 } from 'uuid';

import { isAuthenticatedMSAL, useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    BasicLoader,
    Checkbox,
    ExpandableFileListItem,
    FilledButton,
    Typography,
    UploadFileButton,
    useSnackbar,
    IconButton
} from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, S3 } from "@initia-digital/ts-utils";
import { FileUploadStatus, IFileUpload } from "@initia-digital/types/work-and-safety";

import { useWorkAndSafetyStore } from "../store";
import { PlanActions } from "./PlanActions";
import { IPlanStageProps } from "../interfaces/componentInterfaces";
import { putS3Object, getS3Object } from "../api/objectStorage";
import { SectionHeader } from "./SectionHeader";
import { PlanHeader } from "./PlanHeader";
import { DEFAULT_APP_RESIZE_OPTIONS, scaleImage, DEFAULT_WEB_RESIZE_OPTIONS } from "../utils/files";

const FileUploadItem = memo(({
    fileUpload,
    onUpdate,
    onDelete
}: {
    fileUpload: IFileUpload;
    onUpdate: (id: string, updates: Partial<IFileUpload>) => void;
    onDelete: (id: string) => void;
}) => {
    // ---------------------
    const { id, name, notes, size, includeInAppendix } = fileUpload;
    const isPdf = name.endsWith('.pdf');

    const { queueSnackbar } = useSnackbar();

    const authDetail = useMSALPayload();
    const isAuthenticated = isAuthenticatedMSAL(authDetail);

    const loadFileUrl = useCallback(async () => {
        // ---------------------
        const { id, path } = fileUpload;
        if (
            !baseDefinedAndNotNullNarrowing(path)
            || !baseDefinedAndNotNullNarrowing(id)
        ) throw new Error('File upload is missing id or path');

        const response = await getS3Object(authDetail, { path });
        // ---------------------
        if (response?.buffer) {
            try {
                const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
                const url = URL.createObjectURL(blob);
                return url;
            } catch (error) {
                console.error('Error creating object URL:', error);
                queueSnackbar(`Error processing ${fileUpload.name}`, 'error', 'Error', 3000, '1rem');
            }
        }
        throw new Error('File upload is missing buffer');
    }, [fileUpload, authDetail, queueSnackbar]);
    
    return (
        <Box
            width="100%"
            py="12px"
        >
            <Box border={includeInAppendix ? `2px solid ${green['A700']}` : "2px solid transparent"}>
                <ExpandableFileListItem
                    loadUrl={isAuthenticated ? loadFileUrl : undefined}
                    filename={name ?? ''}
                    notes={notes ?? ''}
                    fileSize={size ?? 0}
                    onFilenameChange={(newFilename) => {
                        onUpdate(id, { name: newFilename });
                    }}
                    onNotesChange={(newNotes) => {
                        onUpdate(id, { notes: newNotes });
                    }}
                    actionButtons={[
                        <IconButton
                            size="small"
                            tooltipTitle="Remove file"
                            onClick={() => onDelete(id)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    ]}
                />
            </Box>
            {
                isPdf &&
                <Box
                    display="flex" flexDirection="row"
                    justifyContent="flex-end"
                >
                    <Checkbox
                        id={`file-upload-${id}-include-in-appendix`}
                        label={`Append PDF to this plan?`}
                        value={includeInAppendix ?? false}
                        handleChange={() => {
                            onUpdate(id, { includeInAppendix: !includeInAppendix });
                        }}
                    />
            </Box>
            }
        </Box>
    );
});

export const SitePlansAndPermits = (props: IPlanStageProps) => {
    // -------------------------------
    const {
        handleSave, handleSaveAndNext,
        handleStatusChange
    } = props;

    const [ areFilesLoading, setAreFilesLoading ] = useState(false);

    const { editPlanState, updateEditPlanState } = useWorkAndSafetyStore();
    const { 
        title, status,
        fileUploads
    } = editPlanState;

    const { queueSnackbar } = useSnackbar();

    const params = useParams();
    const { jobId, planId } = params;

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const authDetail = useMSALPayload();

    const handleFileUpdate = (id: string, updates: Partial<IFileUpload>) => {
        // ---------------------
        updateEditPlanState({
            ...editPlanState,
            fileUploads: fileUploads.map(f =>
                f.id === id ? { ...f, ...updates } : f
            )
        });
    };

    const handleFileDelete = (id: string) => {
        // ---------------------
        updateEditPlanState({
            ...editPlanState,
            fileUploads: editPlanState.fileUploads.filter(fileUpload => fileUpload.id !== id)
        });
    };

    return (
        <Box sx={{ width: '100%' }}>
            <PlanHeader
                id={planId}
                title={title}
                status={status}
            />
            <Box
                display="flex" flexDirection={isSmallerThanSm ? "column" : "row"} flexWrap="wrap"
                alignItems="center" justifyContent="space-between"
                maxWidth="100%"
            >
                <SectionHeader label="Other Files" />
                <PlanActions
                    handleSave={handleSave}
                    handleStatusChange={handleStatusChange}
                />
            </Box>
            <Box display="flex" flexDirection="column" mt="10px" mb="25px">
                <Box>
                    <UploadFileButton                        
                        handleFileSelection={async (files) => {
                            // ---------------------
                            const selectedFiles: IFileUpload[] = [];
                            for (const file of files) {
                                // -----------------------------------------
                                try {
                                    // ------------------------------        
                                    const fileId = uuidv4();
                                    const path = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/plan/file-upload-${fileId}/original/${S3.toSafePath(file.name)}`;

                                    // Upload file to S3
                                    setAreFilesLoading(true);
                                    await putS3Object(authDetail, {
                                        file,
                                        path
                                    });
                                    // Add file to data state
                                    selectedFiles.push({
                                        id: fileId,
                                        path,
                                        name: file.name,
                                        notes: ``,
                                        size: file.size,
                                        jobId: jobId ?? '',
                                        planId: planId ?? '',
                                        uploadStatus: "PENDING" as FileUploadStatus,
                                        includeInAppendix: file.name.endsWith('.pdf') ? true : false
                                    });
                                    setAreFilesLoading(false);

                                    // FOR IMAGES - Handle saving of scaled images for app/web
                                    const isImage = file.type.startsWith('image/');
                                    if (isImage) {
                                        // ---------------------
                                        const appPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/plan/file-upload-${fileId}/app/${S3.toSafePath(file.name)}`;
                                        const webPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/plan/file-upload-${fileId}/web/${S3.toSafePath(file.name)}`;
                        
                                        const appFile = await scaleImage(file, DEFAULT_APP_RESIZE_OPTIONS);
                                        await putS3Object(
                                            authDetail,
                                            {
                                                file: appFile,
                                                path: appPath
                                            }
                                        );

                                        const webFile = await scaleImage(file, DEFAULT_WEB_RESIZE_OPTIONS);
                                        await putS3Object(
                                            authDetail,
                                            {
                                                file: webFile,
                                                path: webPath
                                            }
                                        );
                                    }
                                } catch (err) {
                                    // ------------------------------
                                    console.error(err);
                                    queueSnackbar(`There was an error uploading ${file.name}`, 'error', 'Error', 3000, '1rem');
                                }
                            }
                            updateEditPlanState({
                                ...editPlanState,
                                fileUploads: [...fileUploads, ...selectedFiles]
                            });

                            
                            // Save the plan with file uploads
                            // - This has been added initially to match with aniticipated user expectations
                            await handleSave({
                                ...editPlanState,
                                fileUploads: [...fileUploads, ...selectedFiles]
                            });
                        }}
                    />
                </Box>
                <Box
                    display="flex" flexDirection="column"
                    justifyContent="center"
                    sx={{ maxWidth: '100%' }}
                    py="10px"
                >
                    <Box>
                        <Typography
                            fontSize={"0.9rem"}
                            fontWeight={700}
                            color={theme.palette.primary.contrastText}
                        >
                            File Uploads
                        </Typography>
                    </Box>
                    {
                        areFilesLoading &&
                        <Box 
                            display="flex" flexDirection="row"
                            justifyContent="center" alignItems="center"
                            mt="25px" width="100%"
                        >
                            <BasicLoader
                                size={50}
                            />
                            <Typography
                                fontSize={"0.9rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                Loading files...
                            </Typography>
                        </Box>
                    }
                    {
                        fileUploads
                            .map((fileUpload) => {
                            const { id } = fileUpload;
                            if (
                                !baseDefinedAndNotNullNarrowing(id)
                            ) return <Fragment key={`file-list-item-${id}`}></Fragment>;

                            return (
                                <FileUploadItem
                                    key={`file-list-item-${id}`}
                                    fileUpload={fileUpload}
                                    onUpdate={handleFileUpdate}
                                    onDelete={handleFileDelete}
                                />
                            );
                        })
                    }
                    {
                        fileUploads.length === 0 &&
                        <Box display="flex" flexDirection="row" mt="25px" width="100%" justifyContent="center">
                            <Typography
                                fontSize={"0.9rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                No file uploads for this plan
                            </Typography>
                        </Box>
                    }
                </Box>
            </Box>

            <Box
                width="100%"
                display="flex" flexDirection="row" justifyContent="flex-end"
                pt="12px"
            >
                <FilledButton
                    onClick={handleSaveAndNext}
                    endIcon={<NavigateNextIcon />}
                >
                    Save and Next
                </FilledButton>
            </Box>
        </Box>
    )
}